import React, { useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getIndustryBySlug, getTrainingBySlug } from '../data/industries/industryTrainingsData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Award,
  Users,
  BookOpen,
  Target,
  Calendar,
  TrendingUp,
  Download
} from 'lucide-react';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';
import { SafetyFirstAidDetailContent } from '../components/training/SafetyFirstAidDetailContent';
import { EightDDetailContent } from '../components/training/EightDDetailContent';

export const TrainingProgramDetail = () => {
  const { industrySlug, trainingSlug } = useParams();
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const industry = getIndustryBySlug(industrySlug);
  const training = getTrainingBySlug(industrySlug, trainingSlug);

  // Redirect to canonical page if this training has a dedicated standalone page
  useEffect(() => {
    if (training?.customPath) {
      navigate(training.customPath, { replace: true });
    }
  }, [training, navigate]);

  // While redirecting, render nothing to avoid flash of generic content
  if (training?.customPath) return null;

  if (!industry || !training) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Training Program Not Found</h1>
        <p className="text-gray-600 mb-8">The training program you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/industry-training')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Industries
        </Button>
      </div>
    );
  }

  const TrainingIcon = training.icon;

  // Canonical URL for SEO
  const SITE_URL = 'https://www.isde.academy';
  const canonicalUrl = training.canonicalUrl || `${SITE_URL}/industry-training/${industrySlug}/${trainingSlug}`;

  // Generate breadcrumb structured data for SEO
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Industry Training",
        "item": `${SITE_URL}/industry-training`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": industry.name,
        "item": `${SITE_URL}/industry-training/${industrySlug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": training.title,
        "item": canonicalUrl
      }
    ]
  };

  // Generate course structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": training.title,
    "description": training.description,
    "url": canonicalUrl,
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": SITE_URL
    },
    "courseMode": training.deliveryMode,
    "timeRequired": training.duration,
    "educationalLevel": training.level,
    "about": {
      "@type": "Thing",
      "name": industry.name
    },
    ...(training.geographicArea && {
      "availableLanguage": "English",
      "inLanguage": "en",
      "teaches": training.title,
      "hasCourseInstance": [{
        "@type": "CourseInstance",
        "courseMode": training.deliveryMode,
        "location": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": training.geographicArea
          }
        }
      }]
    }),
    ...(training.certificationLevels && !training.geographicArea && {
      "hasCourseInstance": training.certificationLevels.map(level => ({
        "@type": "CourseInstance",
        "name": `${training.title} - ${level}`,
        "courseMode": training.deliveryMode
      }))
    })
  };

  return (
    <>
      <Helmet>
        <title>{training.metaTitle || `${training.title} | ISDE Academy`}</title>
        <meta
          name="description"
          content={training.metaDescription || training.description}
        />
        <meta
          name="keywords"
          content={training.metaKeywords || `${training.title.toLowerCase()}, ${industry.name.toLowerCase()} training`}
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={training.title} />
        <meta property="og:description" content={training.shortDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>

        {/* Course Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptLTEyIDEyYzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02eiIgZmlsbD0iI2ZmZiIvPjwvZz48L3N2Zz4=')] opacity-20" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-blue-200 mb-4">
              <button onClick={() => navigate('/industry-training')} className="hover:text-white transition-colors">
                Industry Training
              </button>
              <span>/</span>
              <button onClick={() => navigate(`/industry-training/${industry.slug}`)} className="hover:text-white transition-colors">
                {industry.name}
              </button>
              <span>/</span>
              <span className="text-white line-clamp-1">{training.title}</span>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Column - Training Info */}
              <div className="lg:col-span-2">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                    <TrainingIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 leading-tight">{training.title}</h1>
                    <p className="text-base sm:text-lg text-blue-100 mb-3 sm:mb-4 line-clamp-2">{training.shortDescription}</p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 text-xs sm:text-sm">
                        <Clock className="h-3 w-3 mr-1" />
                        {training.duration}
                      </Badge>
                      <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 text-xs sm:text-sm">
                        <Award className="h-3 w-3 mr-1" />
                        {training.level}
                      </Badge>
                      {training.deliveryMode && training.deliveryMode.slice(0, 3).map((mode, idx) => (
                        <Badge key={idx} className="bg-blue-400 text-white border-0 text-xs sm:text-sm">
                          {mode}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - CTA Card */}
              <div className="lg:col-span-1">
                <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white lg:sticky lg:top-24">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4">Ready to Get Started?</h3>
                    <div className="space-y-3">
                      <Button
                        size="lg"
                        className="w-full bg-white text-blue-900 hover:bg-blue-50"
                        onClick={() => navigate('/training-calendar')}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        View Training Calendar
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full border-2 border-white text-white hover:bg-white/10"
                        onClick={() => navigate('/contact')}
                      >
                        Request Information
                      </Button>
                    </div>

                    {training.certification && (
                      <div className="mt-6 pt-6 border-t border-white/20">
                        <p className="text-sm text-blue-100 mb-2">Certification:</p>
                        <p className="font-semibold">{training.certification}</p>
                      </div>
                    )}

                    {training.certificationLevels && (
                      <div className="mt-4">
                        <p className="text-sm text-blue-100 mb-2">Available Levels:</p>
                        <div className="flex flex-wrap gap-2">
                          {training.certificationLevels.map((level, idx) => (
                            <Badge key={idx} className="bg-yellow-400 text-gray-900 border-0">
                              {level}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Training Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {training.description}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comprehensive Safety Training Content - H2, H3, H4 Structure */}
      {trainingSlug === 'safety-first-aid-training' && (
        <SafetyFirstAidDetailContent />
      )}

      {/* 8D Problem Solving Training Content - H2, H3, H4 Structure */}
      {trainingSlug === '8d-training' && (
        <EightDDetailContent />
      )}

      {/* Key Topics Section */}
      {training.keyTopics && training.keyTopics.length > 0 && (
        <section ref={ref} className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Key Topics Covered</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {training.keyTopics.map((topic, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{topic}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Learning Outcomes Section */}
      {training.learningOutcomes && training.learningOutcomes.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Target className="h-6 w-6 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Learning Outcomes</h2>
                </div>

                <div className="space-y-4">
                  {training.learningOutcomes.map((outcome, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100"
                    >
                      <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-gray-700 text-lg">{outcome}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Additional Info Sections */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Training Details */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-blue-600" />
                    Training Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Duration</p>
                    <p className="font-semibold text-gray-900">{training.duration}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Level</p>
                    <p className="font-semibold text-gray-900">{training.level}</p>
                  </div>
                  {training.deliveryMode && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Delivery Mode</p>
                      <div className="flex flex-wrap gap-2">
                        {training.deliveryMode.map((mode, idx) => (
                          <Badge key={idx} variant="outline" className="bg-blue-50">
                            {mode}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {training.examIncluded && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Certification Exam Included</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Additional Info */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    Additional Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {training.recognizedBy && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Recognized By</p>
                      <div className="flex flex-wrap gap-2">
                        {training.recognizedBy.map((org, idx) => (
                          <Badge key={idx} className="bg-green-100 text-green-800">
                            {org}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {training.frameworks && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Frameworks Covered</p>
                      <div className="flex flex-wrap gap-2">
                        {training.frameworks.map((framework, idx) => (
                          <Badge key={idx} variant="outline">
                            {framework}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {training.toolsCovered && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Tools & Software</p>
                      <div className="flex flex-wrap gap-2">
                        {training.toolsCovered.map((tool, idx) => (
                          <Badge key={idx} variant="outline" className="bg-purple-50">
                            {tool}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {training.mandatoryFor && (
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <p className="text-sm font-medium text-orange-800">
                        Mandatory For: {training.mandatoryFor}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Trainings */}
      <UpcomingTrainingsSection
        pageSlug={trainingSlug}
        title={`Upcoming ${training.title} Sessions`}
      />

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Master {training.title}?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join our next training session and earn your certification. Limited seats available!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
                onClick={() => navigate('/training-calendar')}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Register Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => navigate(`/industry-training/${industry.slug}`)}
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                View All {industry.name} Training
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
